import {Center, Checkbox} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {assertAndContinue} from 'Asserts';
import OptionCellDropdown from 'notebook/plain-tables/selectors/OptionCellDropdown';
import {BooleanOptions, getBooleanFromString, getStringFromBoolean} from 'schemas/tags/BooleanUtil';

export enum BooleanCellDisplayMode {
  DROPDOWN = 'DROPDOWN',
  CHECKBOX = 'CHECKBOX',
}
interface BooleanValueCellEditorProps {
  value?: boolean | null;
  onUpdate: (tag: {value: string; booleanValue: boolean | null}) => void;
  isSelected?: boolean;
  locked?: boolean;
  displayMode?: BooleanCellDisplayMode;
}

export default function BooleanValueCellEditor({
  displayMode = BooleanCellDisplayMode.DROPDOWN,
  ...props
}: BooleanValueCellEditorProps): JSX.Element | null {
  const EditorComponent = useMemo(() => {
    switch (displayMode) {
      case BooleanCellDisplayMode.CHECKBOX: {
        return CheckboxBooleanValueCellEditor;
      }
      case BooleanCellDisplayMode.DROPDOWN:
      default: {
        return DropdownBooleanValueCellEditor;
      }
    }
  }, [displayMode]);
  return (
    <Center className="u-position-relative u-height-full">
      <EditorComponent {...props} />
    </Center>
  );
}

function DropdownBooleanValueCellEditor({
  value,
  onUpdate,
  locked,
  isSelected,
}: BooleanValueCellEditorProps): JSX.Element | null {
  const staticDisplay = useMemo(
    () => <div className={css(styles.cellContents)}>{getStringFromBoolean(value)}</div>,
    [value]
  );
  if (locked || isSelected === false) {
    return staticDisplay;
  }
  return (
    <OptionCellDropdown
      locked={locked ?? false}
      isSelected={isSelected ?? true}
      options={Object.values(BooleanOptions)}
      onUpdate={(newValue) => {
        const newBoolean = getBooleanFromString(newValue);
        if (assertAndContinue(newBoolean != null, `Invalid value selected: ${newValue}`)) {
          onUpdate({
            value: newValue,
            booleanValue: newBoolean,
          });
        }
      }}
      staticDisplay={staticDisplay}
      fullWidth={true}
      lightHoverBackground={true}
    />
  );
}

const styles = StyleSheet.create({
  cellContents: {
    textAlign: 'center',
  },
});

function CheckboxBooleanValueCellEditor({
  value,
  onUpdate,
  locked,
}: BooleanValueCellEditorProps): JSX.Element | null {
  if (value == null) {
    return null;
  }
  return (
    <Checkbox
      checked={value}
      onChange={(e) => {
        onUpdate({
          value: e.target.checked ? BooleanOptions.TRUE : BooleanOptions.FALSE,
          booleanValue: e.target.checked,
        });
      }}
      disabled={locked}
    />
  );
}
